import { useEffect, useState } from "react";
import { SurveyServices } from "../services/SurveyServices";
import { handleApiErrorResponses } from "../utils/ApisErrorHandling";
import CloseIcon from "../assets/crossIcon.svg";
import { CircularProgress } from "@mui/material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableItem = ({ id, question, idx, dragHandleProps, getDate }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`py-6 px-8 rounded-[16px] ${
        idx % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"
      }`}
    >
      <div className="grid grid-cols-12 text-[#797979] text-[14px]">
        <div
          {...listeners}
          {...dragHandleProps}
          className="col-span-1 cursor-grab flex items-center justify-center"
        >
          <span className="text-xl">&#x2630;</span>
        </div>
        <div className="col-span-2">{question?.question || "--"}</div>
        <div className="text-center">{question?.sequence || "--"}</div>
        <div>{question?.options?.[0] || "--"}</div>
        <div>{question?.options?.[1] || "--"}</div>
        <div>{question?.options?.[2] || "--"}</div>
        <div>{question?.options?.[3] || "--"}</div>
        <div>{question?.options?.[4] || "--"}</div>
        <div>{question?.options?.[5] || "--"}</div>
        <div>{question?.createdAt ? getDate(question.createdAt) : "--"}</div>
        <div>{question?.updatedAt ? getDate(question.updatedAt) : "--"}</div>
      </div>
    </div>
  );
};

const ManageSequence = ({
  questionList,
  setQuestionList,
  setOpen,
  getQuestionList,
}) => {
  const [items, setItems] = useState([]);
  const [updating, setUpdating] = useState(false);
  useEffect(() => {
    const ids = questionList.map(
      (q) => q.id?.toString() ?? Math.random().toString()
    );
    setItems(ids);
  }, [questionList]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);
  const getDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);

      // Reorder the questions and update sequence
      const newQuestionList = newItems.map((id, idx) => {
        const found = questionList.find((q) => (q.id?.toString() ?? "") === id);
        return { ...found, sequence: idx + 1 };
      });

      setQuestionList(newQuestionList);
    }
  };
  const handleSequenceUpdate = async () => {
    setUpdating(true);
    try {
      const data = questionList?.map((question) => {
        return { id: question.id, sequence: question.sequence };
      });
      const response = await SurveyServices?.updateQuestionSequence(data);
      window.alert(response.data.message);
      await getQuestionList();
      setOpen(false);
    } catch (error) {
      handleApiErrorResponses(error);
    }
    setUpdating(false);
  };

  console.log("items:", items);
  console.log("question list:", questionList);
  return (
    <div className="fixed h-screen w-screen top-0 left-0 bg-[#017c5780] z-[1201]">
      <div className="h-[90%] w-[90%] rounded-[30px] bg-white  mx-auto mt-10">
        <div className="flex items-center justify-end gap-4  px-8 py-4 font-semibold">
          {updating ? (
            <CircularProgress />
          ) : (
            <div
              onClick={handleSequenceUpdate}
              className="text-sm text-white bg-[#017c57] uppercase font-semibold px-8 py-3 rounded-full cursor-pointer"
            >
              Save Updated Sequence
            </div>
          )}
          <img
            src={CloseIcon}
            alt="close-icon"
            onClick={() => setOpen(false)}
            className="cursor-pointer"
          />
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-12 text-[#797979] text-[16px] px-8">
          <div>
            <div className="text-[#191919] text-center font-semibold mb-4">
              Drag
            </div>
          </div>
          <div className="col-span-2">
            <div className="text-[#191919] font-semibold mb-4">Question</div>
          </div>
          <div>
            <div className="text-[#191919] font-semibold text-center mb-4">
              Sequence
            </div>
          </div>
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <div key={n}>
              <div className="text-[#191919] font-semibold mb-4">
                Option {n}
              </div>
            </div>
          ))}
          <div>
            <div className="text-[#191919] font-semibold mb-4">
              Add Date Time
            </div>
          </div>
          <div>
            <div className="text-[#191919] font-semibold mb-4">
              Active Date Time
            </div>
          </div>
        </div>
        <div className="h-[85%] overflow-y-auto rounded-b-[30px]">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items}
              strategy={verticalListSortingStrategy}
            >
              {items.map((id, idx) => {
                const question = questionList.find(
                  (q) => (q.id?.toString() ?? "") === id
                );
                return (
                  <SortableItem
                    key={id}
                    id={id}
                    question={question}
                    idx={idx}
                    getDate={getDate}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>

        {questionList.length === 0 && (
          <div className="text-xl flex items-center justify-center mt-5 font-semibold h-[300px]">
            No Data Found
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageSequence;
